import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keys } from '@/api/queryKeys';
import { useLocationId } from '@/data/useLocationId';
import { birdEyeCellPatchForStatus } from '@/features/bird-eye/adapters/birdEyeAdapters';
import { invalidateBoards, patchBirdEyeCell, refetchAfterStatusChange } from '@/features/bird-eye/hooks/useBirdEye';
import { patchCalendarDayCell } from '@/features/calendar/hooks/patchCalendarDayCell';
import { appointmentsApi } from '../api/appointmentsApi';
import type { AppointmentDetailWire, AppointmentStatusAction } from '../api/types';

export function useAppointmentDetail(id: number | null | undefined) {
  return useQuery({
    queryKey: keys.appointment.detail(id ?? 0),
    queryFn: ({ signal }) => appointmentsApi.getDetail(id!, { signal }),
    enabled: id != null,
    staleTime: 30_000,
  });
}

function useDetailCache(appointmentId: number) {
  const queryClient = useQueryClient();
  const detailKey = keys.appointment.detail(appointmentId);

  return {
    patch: (updater: (detail: AppointmentDetailWire) => AppointmentDetailWire) => {
      queryClient.setQueryData<AppointmentDetailWire>(detailKey, (old) =>
        old ? updater(old) : old,
      );
    },
    refetchDetail: () => {
      void queryClient.invalidateQueries({ queryKey: detailKey });
    },
    /** Status and room drive calendar cells and the Bird-Eye board. */
    refetchBoards: () => {
      invalidateBoards(queryClient);
    },
    /** Puts the new status on the Bird-Eye cell now — the server's read model can lag (`stale`). */
    patchBirdEye: (locationId: number, status: AppointmentStatusAction) => {
      patchBirdEyeCell(queryClient, locationId, appointmentId, birdEyeCellPatchForStatus(status));
      patchCalendarDayCell(queryClient, locationId, appointmentId, status);
    },
    cancelBirdEyePolls: (locationId: number) =>
      queryClient.cancelQueries({ queryKey: keys.birdEye.live(locationId) }),
  };
}

export function useChangeAppointmentStatus(appointmentId: number) {
  const cache = useDetailCache(appointmentId);
  const queryClient = useQueryClient();
  const locationId = useLocationId();
  return useMutation({
    mutationFn: (status: AppointmentStatusAction) =>
      appointmentsApi.changeStatus(appointmentId, status),
    // A poll already in flight would return the pre-change board and overwrite the patch below.
    onMutate: async () => {
      if (locationId != null) await cache.cancelBirdEyePolls(locationId);
    },
    onSuccess: ({ appointment }, status) => {
      cache.patch((detail) => ({ ...detail, appointment }));
      if (locationId != null) cache.patchBirdEye(locationId, status);
      refetchAfterStatusChange(queryClient, appointmentId);
    },
  });
}

export function useChangeAppointmentOperatory(appointmentId: number) {
  const cache = useDetailCache(appointmentId);
  return useMutation({
    mutationFn: (operatoryId: string) => appointmentsApi.changeOperatory(appointmentId, operatoryId),
    onSuccess: ({ operatory }) => {
      cache.patch((detail) => ({ ...detail, operatory }));
      cache.refetchDetail();
      cache.refetchBoards();
    },
  });
}

export function useResendKioskCode(appointmentId: number) {
  return useMutation({
    mutationFn: (channel?: 'sms' | 'email') => appointmentsApi.resendKioskCode(appointmentId, channel),
  });
}

export function useCheckEligibility(appointmentId: number) {
  const cache = useDetailCache(appointmentId);
  return useMutation({
    mutationFn: () => appointmentsApi.checkEligibility(appointmentId),
    onSuccess: ({ insurance }) => {
      cache.patch((detail) => ({ ...detail, insurance }));
      // The producers refresh in the background — pick up their result on the next read.
      cache.refetchDetail();
    },
  });
}
